import { Card } from '@/@cms/types/entities';
import { StudioRoutes } from '@/app/routes';

export type FilterCardPermissionsOptions = {
    /** Whether a feature flag key is enabled for the current user */
    hasFlag?: (key: string) => boolean;
};

/**
 * Filters cards based on feature flags, Studio AI access, and legacy CMS fields.
 *
 * Rules:
 * - Cards with `featureFlag`: visible only when that flag is enabled (no Internal/Clients fallback)
 * - /dani: visible when the user or org has Studio AI / trial access
 * - Legacy Internal / Clients / Users fields still apply when no featureFlag is set
 *
 * @returns true if the card should be displayed to the user
 */
export function filterCardByPermissions(
    card: Card,
    userIsInternal: boolean,
    userEmail: string,
    userSalesforceOrg?: string,
    hasStudioAIAccess?: boolean,
    hasClientStudioAIAccess?: boolean,
    options?: FilterCardPermissionsOptions,
) {
    if (card.featureFlag) {
        return options?.hasFlag?.(card.featureFlag) === true;
    }

    // DANI card visible to users with per-user Studio AI access or trial-enabled orgs.
    if (card.Link === StudioRoutes.STUDIO_AI) {
        return hasStudioAIAccess === true || hasClientStudioAIAccess === true;
    }

    const isInternal = card.Internal === true;
    const hasClients = card.Clients && Array.isArray(card.Clients) && card.Clients.length > 0;
    const hasUsers = card.Users && Array.isArray(card.Users) && card.Users.length > 0;
    const userMatchesClient =
        hasClients &&
        userSalesforceOrg &&
        card.Clients.some((client) => client.ClientName === userSalesforceOrg);
    const userMatchesUsers = hasUsers && card.Users.some((user) => user.Emails === userEmail);

    if (hasUsers) {
        return userMatchesUsers;
    }

    if (isInternal) {
        if (hasClients) {
            return userIsInternal && userMatchesClient;
        } else {
            return userIsInternal;
        }
    } else {
        if (hasClients) {
            return userIsInternal || userMatchesClient;
        } else {
            return true;
        }
    }
}
